<template>
	<PrimeDialog
		:closable="false"
		:maximized="isMobile"
		:style="{ width: '720px' }"
		class="setupWizardDialog"
		modal
		v-model:visible="visibleModel"
	>
		<template #header>
			<div class="setupWizardHeader">
				<div
					class="setupWizardStepper"
					role="tablist"
				>
					<button
						:aria-current="currentStep === index ? 'step' : undefined"
						:class="[
							'setupWizardStepDot',
							{
								'setupWizardStepDot--active': currentStep === index,
								'setupWizardStepDot--visited': index < currentStep,
								'setupWizardStepDot--future': index > currentStep,
							},
						]"
						:disabled="index > currentStep"
						:key="index"
						:title="step.label"
						@click="jumpToStep(index)"
						type="button"
						v-for="(step, index) in steps"
					/>
				</div>
				<button
					:class="['setupWizardDeferLink', { 'setupWizardDeferLink--hidden': isFinalStep }]"
					:disabled="isFinalStep"
					:tabindex="isFinalStep ? -1 : 0"
					@click="onDefer"
					type="button"
				>
					{{ $t('setupWizard_defer') }}
				</button>
			</div>
		</template>

		<div class="setupWizardBody">
			<StepWelcome
				:steps="overviewSteps"
				v-if="currentStepKey === 'welcome'"
			/>
			<StepWorkMode v-else-if="currentStepKey === 'workMode'" />
			<StepAppearance v-else-if="currentStepKey === 'appearance'" />
			<StepButtonsAndLayout v-else-if="currentStepKey === 'buttonsLayout'" />
			<StepFeatures v-else-if="currentStepKey === 'features'" />
			<StepSummary v-else-if="currentStepKey === 'summary'" />
		</div>

		<template #footer>
			<div class="setupWizardFooter">
				<div class="setupWizardFooter__left">
					<PrimeButton
						:label="$t('setupWizard_skip')"
						@click="onSkip"
						severity="secondary"
						text
						v-if="!isFinalStep"
					/>
				</div>
				<div class="setupWizardFooter__right">
					<PrimeButton
						:label="$t('setupWizard_back')"
						@click="goBack"
						severity="secondary"
						text
						v-if="currentStep > 0"
					/>
					<PrimeButton
						:icon="primaryIcon"
						:label="primaryLabel"
						@click="onPrimaryAction"
						icon-pos="right"
					/>
				</div>
			</div>
		</template>
	</PrimeDialog>
</template>

<script lang="ts">
import type { SetupWizardOverviewStep, SetupWizardStep } from 'src/models/setupWizard';

import { computed, defineComponent, watch } from 'vue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import PrimeButton from 'primevue/button';
import PrimeDialog from 'primevue/dialog';
import { useBreakpoint } from 'src/composables/useBreakpoint';
import { useSetupWizard } from 'src/composables/useSetupWizard';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import StepAppearance from 'src/components/setupWizard/steps/StepAppearance.vue';
import StepButtonsAndLayout from 'src/components/setupWizard/steps/StepButtonsAndLayout.vue';
import StepFeatures from 'src/components/setupWizard/steps/StepFeatures.vue';
import StepSummary from 'src/components/setupWizard/steps/StepSummary.vue';
import StepWelcome from 'src/components/setupWizard/steps/StepWelcome.vue';
import StepWorkMode from 'src/components/setupWizard/steps/StepWorkMode.vue';

import { canUseWorkspaceTabsSettings } from 'src/util/platform/browserInteractionCapabilities';

export default defineComponent({
	components: {
		PrimeButton,
		PrimeDialog,
		StepAppearance,
		StepButtonsAndLayout,
		StepFeatures,
		StepSummary,
		StepWelcome,
		StepWorkMode,
	},
	name: 'SetupWizardDialog',
	setup() {
		const { t } = useI18n();
		const { isMobile, width } = useBreakpoint();
		const wizard = useSetupWizard();
		const userInterfaceSettingsStore = useUserInterfaceSettingsStore();

		const workModeStepAvailable = computed<boolean>(() => canUseWorkspaceTabsSettings(width.value));

		// Eine Liste für Fortschrittspunkte UND das Inhaltsverzeichnis auf der Startseite:
		// Schritte mit `caption` sind konfigurierende Schritte und erscheinen in der Übersicht,
		// Willkommen und Abschluss nicht. So kann die Übersicht nicht von der echten Reihenfolge
		// (inkl. dem breitenabhängigen Arbeitsmodus-Schritt) abweichen.
		const steps = computed((): SetupWizardStep[] => {
			const stepList: SetupWizardStep[] = [
				{ key: 'welcome', label: t('setupWizard_welcome_title', { name: '' }) },
			];
			if (workModeStepAvailable.value) {
				stepList.push({
					caption: t('setupWizard_welcome_card_work_mode_caption'),
					key: 'workMode',
					label: t('setupWizard_work_mode_title'),
				});
			}
			stepList.push(
				{
					caption: t('setupWizard_welcome_card_appearance_caption'),
					key: 'appearance',
					label: t('setupWizard_appearance_title'),
				},
				{
					caption: t('setupWizard_welcome_card_layout_caption'),
					key: 'buttonsLayout',
					label: t('setupWizard_layout_title'),
				},
				{
					caption: t('setupWizard_welcome_card_features_caption'),
					key: 'features',
					label: t('setupWizard_features_title'),
				},
				{ key: 'summary', label: t('setupWizard_finish') }
			);
			return stepList;
		});

		const overviewSteps = computed((): SetupWizardOverviewStep[] =>
			steps.value.filter((step): step is SetupWizardOverviewStep => step.caption !== undefined)
		);

		const currentStep = ref<number>(0);

		const boundedStepIndex = computed<number>(() => Math.min(currentStep.value, steps.value.length - 1));

		const currentStepKey = computed<string>(() => steps.value[boundedStepIndex.value]?.key ?? 'welcome');

		const isFinalStep = computed<boolean>(() => boundedStepIndex.value === steps.value.length - 1);

		const visibleModel = computed<boolean>({
			get: () => wizard.isOpen.value,
			set: (value: boolean) => {
				if (!value) {
					wizard.close();
				}
			},
		});

		const primaryLabel = computed<string>(() => {
			if (currentStep.value === 0) {
				return t('setupWizard_get_started');
			}
			if (isFinalStep.value) {
				return t('setupWizard_finish');
			}
			return t('setupWizard_next');
		});

		const primaryIcon = computed<string>(() => {
			if (isFinalStep.value) {
				const solid = userInterfaceSettingsStore.menuItemsSolid;
				return solid ? 'fa-solid fa-check' : 'fa-light fa-check';
			}
			return userInterfaceSettingsStore.menuItemsSolid ? 'fa-solid fa-arrow-right' : 'fa-light fa-arrow-right';
		});

		function goNext(): void {
			if (currentStep.value < steps.value.length - 1) {
				currentStep.value += 1;
			}
		}

		function goBack(): void {
			if (currentStep.value > 0) {
				currentStep.value -= 1;
			}
		}

		function jumpToStep(index: number): void {
			if (index <= currentStep.value) {
				currentStep.value = index;
			}
		}

		function onPrimaryAction(): void {
			if (isFinalStep.value) {
				wizard.complete();
				return;
			}
			goNext();
		}

		function onDefer(): void {
			wizard.defer();
		}

		function onSkip(): void {
			wizard.complete();
		}

		watch(
			() => wizard.isOpen.value,
			(open) => {
				if (open) {
					currentStep.value = 0;
				}
			}
		);

		return {
			currentStep,
			currentStepKey,
			goBack,
			isFinalStep,
			isMobile,
			jumpToStep,
			onDefer,
			onPrimaryAction,
			onSkip,
			overviewSteps,
			primaryIcon,
			primaryLabel,
			steps,
			visibleModel,
		};
	},
});
</script>

<style lang="scss" scoped>
.setupWizardHeader {
	align-items: center;
	display: flex;
	gap: 1rem;
	justify-content: space-between;
	width: 100%;
}

.setupWizardStepper {
	align-items: center;
	display: flex;
	gap: 0.5rem;
}

.setupWizardStepDot {
	background: var(--p-surface-200);
	border: none;
	border-radius: 9999px;
	cursor: pointer;
	height: 0.5rem;
	padding: 0;
	transition:
		background-color 200ms ease,
		transform 200ms ease,
		width 200ms ease;
	width: 0.5rem;

	&:disabled {
		cursor: default;
	}
}

.setupWizardStepDot--active {
	background: var(--p-primary-500);
	width: 1.5rem;
}

.setupWizardStepDot--visited {
	background: var(--p-primary-300);
}

.setupWizardDeferLink {
	background: transparent;
	border: none;
	color: var(--p-text-muted-color);
	cursor: pointer;
	font-size: var(--text-sm);
	padding: 0.25rem 0.5rem;
	transition: color 150ms ease;

	&:hover {
		color: var(--p-text-color);
	}
}

.setupWizardDeferLink--hidden {
	cursor: default;
	pointer-events: none;
	visibility: hidden;
}

.setupWizardBody {
	height: 480px;
	overflow-y: auto;
	padding: 0.75rem 1.75rem 1.5rem;
}

.setupWizardFooter {
	align-items: center;
	display: flex;
	justify-content: space-between;
	width: 100%;
}

.setupWizardFooter__right {
	align-items: center;
	display: flex;
	gap: 0.5rem;
}
</style>

<style lang="scss">
.setupWizardDialog.p-dialog {
	border-radius: 1rem;
	overflow: hidden;
}

.setupWizardDialog .p-dialog-header,
.setupWizardDialog .p-dialog-content,
.setupWizardDialog .p-dialog-footer {
	background: var(--p-surface-0);
}

.setupWizardDialog .p-dialog-content {
	padding: 0;
}

.body--dark .setupWizardDialog .p-dialog-header,
.body--dark .setupWizardDialog .p-dialog-content,
.body--dark .setupWizardDialog .p-dialog-footer {
	background: var(--p-surface-900);
}

.body--dark .setupWizardStepDot {
	background: var(--p-surface-700);
}

.body--dark .setupWizardStepDot--active {
	background: var(--p-primary-400);
}

.body--dark .setupWizardStepDot--visited {
	background: var(--p-primary-600);
}
</style>
